import React from 'react';
import "./list_gaia.scss";
import { Button, ButtonType } from "components/common/Button.component";
import { PencilIcon, Trash2Icon } from "lucide-react";
import { ICON_SIZE } from "components/common/constants";
import { Tooltip } from "components/tooltip/Tooltip";
import { SHARED_TOOLTIP_ID } from "components/tooltip/AppTooltip.component";

const getRowKey = (item, rowIndex) => [item.userId, item.substituteId, item.version, rowIndex].join("-");

export const ListWithActions = React.memo((props) => {

    const {header, values, showActions, translate, language, sortHandle, editRow, deleteRow, scrollAtBottom, emptyMessage} = props;
    const hasRows = values.length > 0;

    // language is in the deps because translate keeps the same identity across language changes
    const headerList = React.useMemo(() => header ? header.map((column, index) => {

        let sortIconClass = column.sortable && column.sortOrder ? " sort-arrow sort-arrow-" + column.sortOrder : "";

        return (<div className={column.class}
                     key={column.value}
                     onClick={column.sortable ? sortHandle.bind(null, index) : null}>
            {translate(column.text)}
            <div className={sortIconClass}/>
        </div>);
    }) : [], [header, sortHandle, translate, language]);

    const listElements = React.useMemo(() => {
        if (!header || !hasRows) {
            return [];
        }
        // identical for every row, so translate once instead of twice per row
        const editLabel = translate("workflows.edit");
        const deleteLabel = translate("workflows.delete");

        return values.map((item, rowIndex) => {
            const rowKey = getRowKey(item, rowIndex);
            let row = header.map((column) => {
                const cellValue = item[column.value];
                const hasTooltip = typeof cellValue === 'string' && cellValue !== '';
                const tooltipId = "list-cell-tooltip-" + rowIndex + "-" + column.value;

                return (<div className={column.class + " align-self-center cropped-text py-3"}
                             key={column.value + "-" + rowKey}
                             data-tip={hasTooltip ? tooltipId : undefined}
                             data-for={SHARED_TOOLTIP_ID}>
                    {cellValue}
                    {hasTooltip && <Tooltip id={tooltipId} content={() => cellValue}/>}
                </div>);
            });

            return (<div className="row" key={rowKey}>
                {row}
                {showActions &&
                    <div className="col-md-1 d-flex justify-content-end py-3">
                        <Button type={ButtonType.GHOST}
                                iconOnly={true}
                                icon={<PencilIcon size={ICON_SIZE.MEDIUM}/>}
                                onClick={(e) => editRow(rowIndex, e)}
                                ariaLabel={editLabel}
                                tooltip={{id: "list-edit-tooltip-" + rowIndex, content: () => editLabel}}
                        />
                        <Button type={ButtonType.GHOST}
                                iconOnly={true}
                                icon={<Trash2Icon size={ICON_SIZE.MEDIUM}/>}
                                onClick={(e) => deleteRow(rowIndex, e)}
                                ariaLabel={deleteLabel}
                                tooltip={{id: "list-delete-tooltip-" + rowIndex, content: () => deleteLabel}}
                        />
                    </div>
                }
            </div>);
        });
    }, [header, values, hasRows, showActions, translate, language, editRow, deleteRow]);

    const scrollList = React.useCallback((event) => {
        if ((parseInt((event.target.scrollTop + event.target.offsetHeight + 1), 10) >= event.target.scrollHeight)) {
            if (scrollAtBottom)
                scrollAtBottom();
        }
    }, [scrollAtBottom]);

    // no rows means no header, but the message keeps the panel chrome: the border and the h3
    // styling both come from .actions-list .main-panel selectors
    if (!hasRows) {
        return (
            <div className="actions-list">
                <div className="main-panel radius-4 d-flex align-items-center justify-content-center">
                    <h3 className="w-100 text-center m-0 py-3">
                        {translate(emptyMessage || "substitutes.noMatches")}
                    </h3>
                </div>
            </div>
        );
    }

    return (
        <div className="actions-list">
            {header &&
                <div className="row header-row mx-0">
                    {headerList}
                    {showActions && (
                        <div className="col-md-1 text-right col-action-buttons">
                            {translate("substitutes.headers.action")}
                        </div>
                    )}
                </div>
            }

            <div className="main-panel radius-4" onScroll={scrollList}>
                {listElements}
            </div>
        </div>
    );
});

ListWithActions.displayName = 'ListWithActions';

export default ListWithActions;
